import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Собственный пикер даты вместо <input type="date">: нативное поле на iOS
// навязывает свою минимальную ширину, из-за чего форма получала горизонтальную
// прокрутку. Здесь поле — обычная строка формы (form-input) на 100% ширины,
// а выбор происходит во вложенном bottom sheet.

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

/** Локальная дата → 'YYYY-MM-DD' (без сдвига через UTC). */
function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** 42 ячейки: понедельник до 1-го числа и далее подряд. */
function buildGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1)
  const mondayOffset = (first.getDay() + 6) % 7
  return Array.from({ length: 42 }, (_, i) =>
    new Date(year, month, 1 - mondayOffset + i),
  )
}

/** 'YYYY-MM-DD' → '16 июня, вторник'. */
function formatDate(value: string): string {
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return 'Выберите дату'
  const date = new Date(y, m - 1, d)
  const dm = date.toLocaleDateString('ru', { day: 'numeric', month: 'long' })
  const wd = date.toLocaleDateString('ru', { weekday: 'long' })
  return `${dm}, ${wd}`
}

// ── Вложенный bottom sheet ──────────────────────────────────────────────────

/**
 * Лёгкий лист для пикеров. Портал в body — чтобы не попасть в stacking context
 * формы и оказаться выше неё. Свайп-закрытие только за ручку сверху, поэтому
 * внутренний скролл не конфликтует. Закрывает только себя, форму не трогает.
 */
export function PickerSheet({ onClose, zIndex, children }: {
  onClose: () => void
  zIndex: number
  children: ReactNode
}) {
  const dragStart = useRef<number | null>(null)
  const [dragY, setDragY] = useState(0)

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  const onTouchStart = (e: React.TouchEvent) => { dragStart.current = e.touches[0].clientY }
  const onTouchMove = (e: React.TouchEvent) => {
    if (dragStart.current === null) return
    setDragY(Math.max(0, e.touches[0].clientY - dragStart.current))
  }
  const onTouchEnd = () => {
    if (dragY > 80) onClose()
    dragStart.current = null
    setDragY(0)
  }

  return createPortal(
    <div className="fixed inset-0 flex flex-col justify-end" style={{ zIndex, transform: 'translateZ(0)' }}>
      <div className="absolute inset-0 bg-black/60 scrim-enter" onClick={onClose} />
      <div
        className="relative bg-surface-1 rounded-t-[20px] flex flex-col sheet-enter overflow-hidden"
        style={{
          maxHeight: '80vh',
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragY ? 'none' : undefined,
        }}
      >
        <div className="shrink-0" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          <div className="sheet-handle" />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

// ── Пикер даты ──────────────────────────────────────────────────────────────

interface DatePickerProps {
  value: string
  onChange: (value: string) => void
  /** z-index листа: должен быть выше формы (70) и тоста (80). */
  zIndex?: number
}

export function DatePicker({ value, onChange, zIndex = 90 }: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(() => {
    const [y, m] = value.split('-').map(Number)
    const t = new Date()
    return { year: y || t.getFullYear(), month: (m ? m - 1 : t.getMonth()) }
  })

  const now = new Date()
  const todayKey = ymd(now)
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const tomorrowKey = ymd(tomorrow)

  const openSheet = () => {
    // Показываем месяц выбранной даты.
    const [y, m] = value.split('-').map(Number)
    if (y && m) setCursor({ year: y, month: m - 1 })
    setOpen(true)
  }

  const shiftMonth = (delta: -1 | 1) => {
    setCursor(c => {
      const d = new Date(c.year, c.month + delta, 1)
      return { year: d.getFullYear(), month: d.getMonth() }
    })
  }

  const pick = (key: string) => {
    onChange(key)
    setOpen(false)
  }

  const cells = buildGrid(cursor.year, cursor.month)

  return (
    <>
      <button type="button" className="form-input text-left" onClick={openSheet}>
        {formatDate(value)}
      </button>

      {open && (
        <PickerSheet onClose={() => setOpen(false)} zIndex={zIndex}>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
            {/* Быстрый выбор */}
            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => pick(todayKey)} className="btn-secondary flex-1">
                Сегодня
              </button>
              <button type="button" onClick={() => pick(tomorrowKey)} className="btn-secondary flex-1">
                Завтра
              </button>
            </div>

            {/* Переключение месяцев */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                className="btn-active flex items-center justify-center rounded-lg bg-surface-2 border border-line-1 text-ink-1"
                style={{ width: 32, height: 32 }}
                aria-label="Предыдущий месяц"
              >
                <ChevronLeft size={17} />
              </button>
              <span className="text-base text-ink">
                {MONTHS[cursor.month]} {cursor.year}
              </span>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                className="btn-active flex items-center justify-center rounded-lg bg-surface-2 border border-line-1 text-ink-1"
                style={{ width: 32, height: 32 }}
                aria-label="Следующий месяц"
              >
                <ChevronRight size={17} />
              </button>
            </div>

            {/* Дни недели */}
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map(w => (
                <div key={w} className="text-center text-ink-2" style={{ fontSize: 11 }}>
                  {w}
                </div>
              ))}
            </div>

            {/* Сетка месяца */}
            <div className="grid grid-cols-7 gap-0.5">
              {cells.map(date => {
                const key = ymd(date)
                const outside = date.getMonth() !== cursor.month
                const isToday = key === todayKey
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => pick(key)}
                    className={`cal-cell ${outside ? 'outside' : ''} ${key === value ? 'selected' : ''}`}
                  >
                    <span
                      className={isToday ? 'cal-today-circle' : ''}
                      style={{ fontSize: 15, color: isToday ? '#fff' : 'var(--color-text-0)' }}
                    >
                      {date.getDate()}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </PickerSheet>
      )}
    </>
  )
}
